import axios from 'axios';
import { getJwtToken } from '../services/jwt.services';
import csvDownload from 'json-to-csv-export';
import companyService from './company.services'
import JSZip from 'jszip';
import logger from '../utils/logger';
const rateLimit = require('axios-rate-limit');
const axiosRateLimited = rateLimit(axios.create(), { maxRPS: 1 }); // No more than 1 request per second
const BASE_URL = process.env.REACT_APP_BASE_URL; // Replace with your API's base URL
const outreachStatsBatchSize = 20 // Increased from 10 to 20
const exportContactsBatchSize = 20 // Lists per export request; batched to avoid timeouts/oversized payloads on large exports

const MediaRelationsService = {
  getLists: async (companyid) => {
    const jwtToken = getJwtToken();
    try {
      const apiUrl = `${BASE_URL}/mr/${companyid}/lists`;
      const headers = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      };
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): getLists ", error.message);
      throw error;
    }
  },
  getListsPage: async (companyid, page, retries = 3) => {
    const jwtToken = getJwtToken();
    const apiUrl = `${BASE_URL}/mr/${companyid}/lists/${page}`;
    const headers = {
      'Authorization': `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    };
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await axiosRateLimited.get(apiUrl, { headers });
        return response.data;
      } catch (error) {
        if (attempt < retries) {
          // 1s, 2s, 4s back-off before next attempt
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        } else {
          logger.error("MediaRelationsService (ERROR): getListsPage ", error.message);
          throw error;
        }
      }
    }
  },
  exportContacts: async (payload, companyid, isAdmin = false, onProgress) => {
    const jwtToken = getJwtToken();
    try {
      logger.info("MediaRelationsService.exportContacts:", { companyid, isAdmin, listCount: payload?.mediaLists?.length });

      const apiUrl = isAdmin ? `${BASE_URL}/mr/${companyid}/exportallcontacts` : `${BASE_URL}/mr/${companyid}/exportcontacts`;
      const headers = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json'
      };

      const mediaLists = Array.isArray(payload?.mediaLists) ? payload.mediaLists : [];

      // Small exports go in a single request, preserving the original behaviour.
      if (mediaLists.length <= exportContactsBatchSize) {
        const response = await axios.post(apiUrl, payload, { headers });
        return response.data; // Contains { status: "success", lists: listExports }
      }

      // Large exports are split into batches to avoid gateway timeouts and
      // oversized request/response bodies. Each batch returns a `lists` object
      // keyed by list id, which we merge into one combined result before zipping.
      const totalBatches = Math.ceil(mediaLists.length / exportContactsBatchSize);
      const mergedLists = {};
      let status = "success";

      for (let i = 0; i < mediaLists.length; i += exportContactsBatchSize) {
        const batchNumber = Math.floor(i / exportContactsBatchSize) + 1;
        const batchPayload = { ...payload, mediaLists: mediaLists.slice(i, i + exportContactsBatchSize) };

        const response = await axiosRateLimited.post(apiUrl, batchPayload, { headers });
        if (response.data?.lists) {
          Object.assign(mergedLists, response.data.lists);
        }
        if (response.data?.status && response.data.status !== "success") {
          status = response.data.status;
        }
        if (typeof onProgress === "function") {
          onProgress(batchNumber, totalBatches);
        }
      }

      return { status, lists: mergedLists };
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): exportContacts ", error.message);
      throw error;
    }
  },
  saveCsv: async (response, zipFileName) => {
    if (response.lists) {
        const zip = new JSZip();
        
        Object.entries(response.lists).forEach(([listId, listData]) => {
            const fileName = `${listData.name.replace(/[^a-z0-9]/gi, '_')}_${listId}.csv`;
            zip.file(fileName, listData.csvData);
        });
        
        const zipBlob = await zip.generateAsync({type: 'blob'});
        const element = document.createElement('a');
        element.href = URL.createObjectURL(zipBlob);
        element.download = `${zipFileName}.zip`;
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        URL.revokeObjectURL(element.href);
    }
  },
  copyContacts: async (payload, sourceCompanyId, targetCompanyId) => {
    const jwtToken = getJwtToken();
    try {
      logger.info("MediaRelationsService.copyContacts:", { payload, sourceCompanyId, targetCompanyId });
      const companyResponse = await companyService.getCompany(targetCompanyId);
        if (companyResponse.status === "fail") {
            throw new Error(`Invalid company ID`);
        }
      const apiUrl = `${BASE_URL}/mr/${sourceCompanyId}/copylists/${targetCompanyId}`;
      const headers = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      };
      const response = await axios.post(apiUrl, payload, { headers });
      return response.data; // Contains { status: "success", lists: listExports }
    } catch (error) {
        logger.error("MediaRelationsService (ERROR): copyContacts ", error.message);
        throw error;
    }
  },
  getOutreachEmails: async (companyid) => {
    const jwtToken = getJwtToken();
    try {
      const apiUrl = `${BASE_URL}/mr/${companyid}/outreach`;
      const headers = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      };
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): getOutreachEmails ", error.message);
      throw error;
    }
  },
  getOutreachRecipients: async (companyId, outreachId) => {
    const jwtToken = getJwtToken();
    try {
      const apiUrl = `${BASE_URL}/mr/${companyId}/outreach/${outreachId}/recipients`;
      const headers = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      };
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): getOutreachRecipients ", error.message);
      throw error;
    }
  },
  cleanupMediaListSession: async (companyId) => {
    const jwtToken = getJwtToken();
    try {
      const apiUrl = `${BASE_URL}/mr/${companyId}/lists/session`;
      const headers = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json',
      };
      await axios.delete(apiUrl, { headers });
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): cleanupMediaListSession ", error.message);
    }
  },
  createMediaList: async (companyId, payload) => {
    const jwtToken = getJwtToken();
    try {
      const apiUrl = `${BASE_URL}/mr/${companyId}/lists/restore`;
      const headers = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      };
      const response = await axios.post(apiUrl, payload, { headers })
      return response.data;
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): createMediaList", error.message);
      throw error;
    }
  },
  getNewswires: async (companyid) => {
    const jwtToken = getJwtToken();
    try {
      const apiUrl = `${BASE_URL}/mr/${companyid}/newswire`;
      const headers = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      };
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): getNewswires ", error.message);
      throw error;
    }
  },
  getNewswireOrgs: async (companyid) => {
    const jwtToken = getJwtToken();
    try {
      const apiUrl = `${BASE_URL}/mr/${companyid}/newswire/orgs`;
      const headers = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      };
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): getNewswireOrgs ", error.message);
      throw error;
    }
  },
  deleteNewswireOrg: async (companyid, orgId) => {
    const jwtToken = getJwtToken();
    try {
      const apiUrl = `${BASE_URL}/mr/${companyid}/newswire/orgs/${orgId}`;
      const headers = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json', // Adjust the content type if needed
      };
      const response = await axios.delete(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): getNewswireOrgs ", error.message);
      throw error;
    }
  },
  exportOutreachStats: async (payload, companyid) => {
    const jwtToken = getJwtToken();
    const apiUrl = `${BASE_URL}/mr/${companyid}/outreach/stats`;
    const headers = {
      'Authorization': `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    };
    try {
      let promises = []
      let stats = []
      logger.info("Fetching outreach stats for IDs:", payload.outreachIds);
      
      for (let i = 0; i < payload.outreachIds.length; i += outreachStatsBatchSize) {
        let newPayload = { outreachIds: payload.outreachIds.slice(i, i + outreachStatsBatchSize) }
        let promise = axiosRateLimited.post(apiUrl, newPayload, { headers });
        promises.push(promise);
      }
      
      const responses = await Promise.all(promises);
      let currentIndex = 0;
      responses.forEach((response, batchIndex) => {
        logger.debug("Raw response data for batch " + batchIndex + ":", JSON.stringify(response.data));
        if (response.data && response.data.data) {
          if (Array.isArray(response.data.data)) {
            // Add IDs from the original request to each stat object
            const statsWithIds = response.data.data.map((stat, i) => ({
              ...stat,
              outreachId: payload.outreachIds[currentIndex + i]
            }));
            stats.push(...statsWithIds);
            currentIndex += response.data.data.length;
          } else {
            logger.error("Invalid stats data format in batch " + batchIndex + ":", {
              actualType: typeof response.data.data,
              data: response.data
            });
          }
        } else {
          logger.error("Missing data in response for batch " + batchIndex + ":", response.data);
        }
      });
      
      logger.info("Processed all stats:", {
        totalStats: stats.length,
        sampleStats: stats.length > 0 ? stats[0] : null,
        allKeys: stats.length > 0 ? Object.keys(stats[0]) : []
      });
      return { data: stats };
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): exportOutreachStats ", error.message);
      throw error;
    }
  },

  saveOutreachCsv: async (data, fileName, outreachData) => {
    if (!Array.isArray(data)) {
      logger.error("MediaRelationsService (ERROR): saveOutreachCsv - data is not an array", { data });
      throw new Error("Invalid data format: Expected an array");
    }

    logger.info("Received data for CSV:", { count: data.length });

    // Create a map of outreach IDs to subjects for quick lookup
    const subjectMap = {};
    if (outreachData && Array.isArray(outreachData)) {
      outreachData.forEach(item => {
        subjectMap[item.id] = item.subject;
      });
    }

    logger.debug("Full data structure sample:", {
      firstItem: data[0],
      availableKeys: data[0] ? Object.keys(data[0]) : [],
      sampleValues: data[0] ? {
        subject: data[0].subject,
        fromAddress: data[0].fromAddress,
        delivered: data[0].delivered,
        open: data[0].open,
        unopened: data[0].unopened,
        total: data[0].total
      } : null
    });

    const exportData = data.map((oneitem) => {
      if (!oneitem) {
        logger.error("MediaRelationsService (ERROR): saveOutreachCsv - item is undefined");
        throw new Error("Invalid data format: Item is undefined");
      }

      logger.debug("Processing outreach item:", { 
        id: oneitem.outreachId,
        rawStats: {
          delivered: oneitem.delivered,
          open: oneitem.open,
          unopened: oneitem.unopened,
          bounce: oneitem.bounce,
          click: oneitem.click,
          blocked: oneitem.blocked,
          openRate: oneitem.openRate,
          ctoRate: oneitem.ctoRate
        }
      });

      return {
        outreach_id: oneitem.outreachId || '',
        subject: subjectMap[oneitem.outreachId] || oneitem.subject || '',  // Use mapped subject or fallback
        delivered: oneitem.delivered || 0,
        bounces: oneitem.bounce || 0,
        blocked: oneitem.blocked || 0,
        clicks: oneitem.click || 0,
        opens: oneitem.open || 0,
        unopened: oneitem.unopened || 0,
        open_rate: oneitem.openRate || 0,
        cto_rate: oneitem.ctoRate || 0,
        hard_bounces: oneitem.hardBounces || 0,
        soft_bounces: oneitem.softBounces || 0,
        other_bounces: oneitem.otherBounces || 0,
        untracked: oneitem.untracked || 0,
      }
    });

    logger.debug("Preparing CSV with data:", { rows: exportData.length, columns: Object.keys(exportData[0]) });

    const options = {
      data: exportData,
      delimiter: ',',
      filename: fileName
    }

    try {
      csvDownload(options);
      logger.info("CSV download initiated successfully");
    } catch (error) {
      logger.error("MediaRelationsService (ERROR): saveOutreachCsv - failed to download CSV", error);
      throw error;
    }
  }
};

export default MediaRelationsService;